import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import useAdminAuth from '../hooks/useAdminAuth';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Camera, Plus, Trash2, Upload, CheckCircle, Edit2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTedsTravels = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getAdminEmail } = useAdminAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [photoId, setPhotoId] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editCaption, setEditCaption] = useState('');
  const fileRef = useRef(null);

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/teds-travels`);
      setPosts(res.data);
    } catch { toast({ title: 'Error', description: 'Failed to load posts', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast({ title: 'Error', description: 'File must be under 10MB', variant: 'destructive' }); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        setPhotoPreview(base64);
        const res = await axios.post(`${API}/teds-travels/upload-photo`, { file_data: base64 });
        setPhotoId(res.data.file_id);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch { setUploading(false); toast({ title: 'Error', description: 'Upload failed', variant: 'destructive' }); }
  };

  const handleSubmit = async () => {
    if (!photoId) { toast({ title: 'Error', description: 'Please upload a photo', variant: 'destructive' }); return; }
    if (caption.length > 300) { toast({ title: 'Error', description: 'Caption must be 300 characters or less', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/teds-travels`, { photo_id: photoId, caption, admin_email: getAdminEmail() });
      toast({ title: 'Posted!', description: 'Photo added to Truckn Ted\'s Travels' });
      setShowForm(false); setPhotoId(null); setPhotoPreview(null); setCaption('');
      fetchPosts();
    } catch (error) { toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to post', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/teds-travels/${id}`);
      toast({ title: 'Deleted' });
      fetchPosts();
    } catch { toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' }); }
  };

  const handleUpdateCaption = async (id) => {
    if (editCaption.length > 300) { toast({ title: 'Error', description: 'Caption must be 300 characters or less', variant: 'destructive' }); return; }
    try {
      await axios.put(`${API}/teds-travels/${id}`, { caption: editCaption });
      toast({ title: 'Updated' });
      setEditingId(null);
      fetchPosts();
    } catch { toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' }); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#1e3a5f]">Truckn Ted's Travels</h1>
              <p className="text-gray-600 mt-1">Manage travel photo album</p>
            </div>
            <div className="flex gap-2">
              {!showForm && (
                <Button onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="add-post-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Photo
                </Button>
              )}
              <Button onClick={() => navigate('/admin')} variant="outline">Back to Dashboard</Button>
            </div>
          </div>

          {/* New Post Form */}
          {showForm && (
            <Card className="p-6 mb-8 border-2 border-red-200">
              <h2 className="text-lg font-bold text-[#1e3a5f] mb-4">New Photo Post</h2>
              <div className="space-y-4">
                <div>
                  <Label>Photo *</Label>
                  <div className="mt-2">
                    {photoPreview ? (
                      <div className="relative inline-block">
                        <img src={photoPreview} alt="Preview" className="max-h-60 rounded-lg border" />
                        <button onClick={() => { setPhotoId(null); setPhotoPreview(null); }} className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">&times;</button>
                      </div>
                    ) : (
                      <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
                        <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Uploading...' : 'Choose Photo'}
                      </Button>
                    )}
                    <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" onChange={handlePhotoUpload} className="hidden" />
                  </div>
                </div>
                <div>
                  <Label>Caption ({caption.length}/300)</Label>
                  <Textarea value={caption} onChange={(e) => setCaption(e.target.value.slice(0, 300))} rows={3} placeholder="What's the story behind this photo?" maxLength={300} className="mt-1" data-testid="post-caption" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSubmit} disabled={submitting || !photoId} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-post-btn">
                    {submitting ? 'Posting...' : 'Post Photo'}
                  </Button>
                  <Button variant="outline" onClick={() => { setShowForm(false); setPhotoId(null); setPhotoPreview(null); setCaption(''); }}>Cancel</Button>
                </div>
              </div>
            </Card>
          )}

          {/* Posts Grid */}
          <div className="text-sm text-gray-500 mb-4">{posts.length} post{posts.length !== 1 ? 's' : ''}</div>
          {loading ? <p className="text-center py-8">Loading...</p> : posts.length === 0 ? (
            <Card className="p-12 text-center"><Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-600">No posts yet. Add your first photo!</p></Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {posts.map(post => (
                <Card key={post.id} className="overflow-hidden" data-testid={`admin-ted-${post.id}`}>
                  <img src={post.photo_data} alt={post.caption || ''} className="w-full h-48 object-cover" />
                  <div className="p-4">
                    {editingId === post.id ? (
                      <div className="space-y-2">
                        <Textarea value={editCaption} onChange={(e) => setEditCaption(e.target.value.slice(0, 300))} rows={2} maxLength={300} />
                        <p className="text-xs text-gray-400">{editCaption.length}/300</p>
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => handleUpdateCaption(post.id)} className="bg-green-600 text-white text-xs">Save</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="text-xs">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-700 mb-2">{post.caption || <span className="italic text-gray-400">No caption</span>}</p>
                        <p className="text-xs text-gray-400 mb-3">{new Date(post.created_at).toLocaleDateString('en-AU')}</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => { setEditingId(post.id); setEditCaption(post.caption || ''); }} className="text-xs">
                            <Edit2 className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(post.id)} className="text-xs text-red-600 border-red-300">
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminTedsTravels;
